import type { TaskListItem } from '../../shared/types/task';
import {
  formatSlaSmartLabel,
  getRemainingMinutesFromDeadline,
  getTaskTotalSlaMinutes,
} from '../../shared/utils/operationalRisk';

export type UrgencyLevel = 'overdue' | 'today' | 'normal';

export interface UrgencyInfo {
  level: UrgencyLevel;
  label: string;
  detail: string;
  badgeClass: string;
}

const PRIORITY_LABELS: Record<string, string> = {
  critica: 'Crítica',
  alta: 'Alta',
  media: 'Média',
  baixa: 'Baixa',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Aguardando',
  waiting: 'Na fila',
  in_progress: 'Em execução',
  paused: 'Pausada',
  completed: 'Concluída',
  failed: 'Falha',
  skipped: 'Pulada',
};

export function resolveTaskRequesterFromList(task: TaskListItem): string {
  const meta = (task.metadata ?? {}) as Record<string, unknown>;
  const candidates = [
    meta.initiated_by_name,
    meta.requester_name,
    meta.created_by_name,
    meta.requester,
  ];
  const resolved = candidates.find((v) => typeof v === 'string' && v.trim().length > 0);
  return typeof resolved === 'string' ? resolved.trim() : '—';
}

export function resolveTaskDepartment(task: TaskListItem): string {
  const meta = (task.metadata ?? {}) as Record<string, unknown>;
  const candidates = [meta.department_name, meta.department, meta.sector, meta.setor];
  const resolved = candidates.find((v) => typeof v === 'string' && v.trim().length > 0);
  return typeof resolved === 'string' ? resolved.trim() : '—';
}

export function formatTaskDateShort(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export function formatPriorityLabel(priority?: string | null): string {
  if (!priority) return '—';
  return PRIORITY_LABELS[priority.toLowerCase()] ?? priority.charAt(0).toUpperCase() + priority.slice(1);
}

export function formatStatusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

export function resolveUrgency(task: TaskListItem): UrgencyInfo {
  const remainingMinutes = getRemainingMinutesFromDeadline(task.slaDeadline);
  const slaInfo = formatSlaSmartLabel(remainingMinutes);
  const isOverdue =
    task.isOverdue ||
    slaInfo.status === 'violated' ||
    (remainingMinutes !== null && remainingMinutes < 0);

  if (isOverdue) {
    const overdueMinutes =
      remainingMinutes !== null && remainingMinutes < 0 ? Math.abs(remainingMinutes) : 0;
    const days = Math.floor(overdueMinutes / (60 * 24));
    const hours = Math.floor((overdueMinutes % (60 * 24)) / 60);
    const detail =
      days > 0 ? `${days} dia${days > 1 ? 's' : ''} de atraso` : `${hours}h de atraso`;
    return {
      level: 'overdue',
      label: 'ATRASADA',
      detail,
      badgeClass:
        'bg-red-600 text-white border-red-700 shadow-[0_0_0_1px_rgba(220,38,38,0.4)]',
    };
  }

  const totalSla = getTaskTotalSlaMinutes(task);
  const dueToday =
    remainingMinutes !== null &&
    remainingMinutes >= 0 &&
    remainingMinutes <= 24 * 60;

  const atRisk =
    remainingMinutes !== null &&
    totalSla !== null &&
    totalSla > 0 &&
    remainingMinutes <= totalSla * 0.25;

  if (dueToday || atRisk) {
    return {
      level: 'today',
      label: 'HOJE',
      detail: slaInfo.status === 'none' ? 'Vence hoje' : slaInfo.label,
      badgeClass: 'bg-amber-500 text-white border-amber-600',
    };
  }

  return {
    level: 'normal',
    label: 'NORMAL',
    detail: slaInfo.status === 'none' ? 'Sem prazo' : slaInfo.label,
    badgeClass: 'bg-emerald-600 text-white border-emerald-700',
  };
}

export function resolveTaskClient(task: TaskListItem): string {
  const meta = (task.metadata ?? {}) as Record<string, unknown>;
  const candidates = [
    meta.client_name,
    meta.customer_name,
    meta.cliente,
    meta.customer,
    task.instanceTitle,
  ];
  const resolved = candidates.find((v) => typeof v === 'string' && v.trim().length > 0);
  return typeof resolved === 'string' ? resolved.trim() : '—';
}

export function resolveTaskResponsible(task: TaskListItem): string {
  return (
    task.assignedUserName ??
    task.assignedToName ??
    (typeof task.metadata?.executor_name === 'string' ? task.metadata.executor_name : null) ??
    '—'
  );
}

export function formatRelativeTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `há ${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `há ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'ontem';
  return `há ${days}d`;
}

export function resolveTaskIdleLabel(task: TaskListItem): string {
  if (task.queueDuration != null && task.queueDuration > 0) {
    return formatIdleDurationLabel(task.queueDuration);
  }
  if (task.status === 'in_progress' && task.startedAt) {
    return formatRelativeTime(task.updatedAt ?? task.startedAt);
  }
  if (task.status !== 'in_progress' && task.status !== 'completed') {
    return formatRelativeTime(task.updatedAt ?? task.createdAt);
  }
  return '—';
}

export function formatLauncherLastExecution(value?: string | null): string {
  if (!value) return 'Nunca';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Hoje';
  if (diffDays === 1) return 'Ontem';
  if (diffDays < 7) return `Há ${diffDays} dias`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export function formatIdleDurationLabel(idleMinutes?: number | null): string {
  if (idleMinutes == null || idleMinutes <= 0) return '—';
  const days = Math.floor(idleMinutes / (60 * 24));
  if (days >= 1) return `${days} dia${days > 1 ? 's' : ''}`;
  const hours = Math.floor(idleMinutes / 60);
  if (hours >= 1) return `${hours}h`;
  return `${Math.round(idleMinutes)} min`;
}

export interface TimelineEventLike {
  id: string;
  dateLabel: string;
  title: string;
  detail?: string;
  tone?: 'default' | 'ok' | 'warning' | 'danger' | 'info';
}

interface ExecutionLogLike {
  id: string;
  action: string;
  justification?: string;
  metadata?: Record<string, unknown>;
  created_at?: string;
}

function resolveLogTimelineTitle(log: ExecutionLogLike): string {
  const meta = log.metadata ?? {};
  if (log.action === 'status_change') {
    const from = meta.from as string | undefined;
    const to = meta.to as string | undefined;
    if (to === 'in_progress' && from === 'pending') return 'Execução iniciada';
    if (to === 'in_progress' && from === 'paused') return 'Execução retomada';
    if (to === 'paused') return 'Execução pausada';
    if (to === 'completed') return 'Etapa concluída';
    if (to === 'failed') return meta.result === 'rejected' ? 'Devolvida' : 'Falha registrada';
    return 'Status alterado';
  }
  const labels: Record<string, string> = {
    claim: 'Atribuída ao executor',
    reassign: 'Transferida',
    escalate: 'Escalada',
    return: 'Devolvida',
    note: 'Observação registrada',
    register_problem: 'Problema registrado',
    complete: 'Concluída',
    cancel: 'Cancelada',
    attachment: 'Anexo adicionado',
  };
  return labels[log.action] ?? log.action;
}

function resolveLogTimelineTone(log: ExecutionLogLike): TimelineEventLike['tone'] {
  if (log.action === 'register_problem') return 'danger';
  if (log.action === 'complete' || log.action === 'status_change' && log.metadata?.to === 'completed') return 'ok';
  if (log.action === 'escalate' || log.action === 'status_change' && log.metadata?.to === 'paused') return 'warning';
  if (log.action === 'claim' || log.action === 'status_change' && log.metadata?.to === 'in_progress') return 'info';
  return 'default';
}

export function buildExecutionTimeline(createdAt: string, logs: ExecutionLogLike[]): TimelineEventLike[] {
  const events: TimelineEventLike[] = [
    {
      id: 'created',
      dateLabel: formatTaskDateShort(createdAt),
      title: 'Solicitação criada',
      tone: 'info',
    },
  ];

  const sorted = [...logs].sort((a, b) => {
    const ta = new Date(a.created_at ?? 0).getTime();
    const tb = new Date(b.created_at ?? 0).getTime();
    return ta - tb;
  });

  for (const log of sorted) {
    events.push({
      id: log.id,
      dateLabel: formatTaskDateShort(log.created_at),
      title: resolveLogTimelineTitle(log),
      detail: log.justification,
      tone: resolveLogTimelineTone(log),
    });
  }

  return events;
}

export function buildRecentMovementEvents(createdAt: string, logs: ExecutionLogLike[], limit = 5): TimelineEventLike[] {
  return [...buildExecutionTimeline(createdAt, logs)].reverse().slice(0, limit);
}

export function formatLauncherAvgDuration(flow: { usageCount: number }): string {
  if (flow.usageCount <= 0) return '—';
  if (flow.usageCount < 10) return '< 1 semana';
  if (flow.usageCount < 50) return '1–3 dias';
  return '2–5 dias';
}
